import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AdminShell from '../Layout/AdminShell';
import {
  PageHead, Panel, Pill, StatRow, StatCard, Select, Modal, ConfirmModal,
  FormField, FormSection, btn, field,
} from '../common/AdminUI';
import ShipmentFilters from '../common/ShipmentFilters';
import { matchesShipmentSearch } from '../../utils/shipmentSearch';
import SuperAdminUsers from './SuperAdminUsers';
import AddUserPage from './AddUserPage';
import ClientMaster from '../common/ClientMaster';
import { CustomerInvoicesModal } from '../common/CustomerInvoices';
import InvoiceGenerationPage from '../Dashboard/InvoiceGenerationPage';
import ShipmentsTable from '../common/ShipmentsTable';
import SeaExportTableReadOnly from './SeaExportTableReadOnly';
import PendingApprovalQueue from './PendingApprovalQueue';
import { UserPlus, Users, Package, Edit, Trash2, Eye, EyeOff, RefreshCw, Download, Calendar, FileText, BarChart3, TrendingUp, IndianRupee, PieChart, Inbox, Hash, Receipt, Search, X, ChevronDown, RotateCcw, UserCog, KeyRound, Save, User as UserIcon } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, apiCall } from '../../config/api';
import { hashPassword } from '../../utils/crypto';
import { useAuth } from '../../context/AuthContext';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  DATE_PRESETS, PaymentStatusFilter, UnpaidSinceFilter, OverdueDaysFilter, makePaymentMatcher,
} from '../common/InvoiceFilters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, LineChart, Line, Area, AreaChart, LabelList } from 'recharts';
import { getServices } from '../../config/servicesSource';
import { exportShipmentsExcel } from '../../utils/exportShipmentsExcel';
import HsnCodeManager from '../common/HsnCodeManager';
import { escapeCsv } from '../../utils/exportCsv';

// Helper functions for data formatting
const parseField = (field) => {
  if (field === null || field === undefined || field === '') return '-';
  if (typeof field === 'string') {
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed.join(', ') : parsed;
    } catch {
      return field;
    }
  }
  return field;
};

const joinArray = (arr) => {
  if (!arr || !Array.isArray(arr)) return '-';
  return arr.filter(item => item !== null && item !== undefined && item !== '').join(' & ') || '-';
};

const joinUniqueArray = (arr) => {
  if (!arr || !Array.isArray(arr)) return '-';
  const uniqueValues = [...new Set(arr.filter(item => item !== null && item !== undefined && item !== ''))];
  return uniqueValues.join(' & ') || '-';
};

// Helper function to format dates - convert to DD/MM/YYYY
const formatDates = (arr) => {
  if (!arr || !Array.isArray(arr)) return '-';
  const filtered = arr.filter(item => item !== null && item !== undefined && item !== '');
  
  if (filtered.length === 0) return '-';
  
  // Month mapping
  const monthMap = {
    'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
    'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
  };
  
  // Convert dates to DD/MM/YYYY format first
  const formattedDates = filtered.map(date => {
    if (!date) return null;
    let dateStr = date;
    // Remove time part if present
    if (typeof date === 'string' && date.includes(' ')) {
      dateStr = date.split(' ')[0];
    }
    
    // Check if already in DD/MM/YYYY format
    const ddmmyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateStr.match(ddmmyyyyPattern);
    if (match) {
      return dateStr; // Already in correct format
    }
    
    // Check for DD-MMM-YYY or DD-MMM-YYYY format (e.g., "15-NOV-025")
    const ddmmmyyyPattern = /^(\d{2})-(\w{3})-(\d{2,4})$/;
    const mmmMatch = dateStr.match(ddmmmyyyPattern);
    if (mmmMatch) {
      const day = parseInt(mmmMatch[1]);
      const monthStr = mmmMatch[2].toUpperCase();
      let year = parseInt(mmmMatch[3]);
      
      // Fix year: 025 -> 2025, 25 -> 2025
      if (year < 100) {
        year += 2000;
      }
      
      const month = monthMap[monthStr];
      if (month !== undefined) {
        return `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${year}`;
      }
    }
    
    // Parse and format to DD/MM/YYYY
    try {
      const dateObj = new Date(dateStr);
      if (!isNaN(dateObj.getTime())) {
        let day = dateObj.getDate();
        let month = dateObj.getMonth() + 1;
        let year = dateObj.getFullYear();
        
        // Fix for years like 025 (should be 2025)
        if (year < 100) {
          year += 2000;
        }
        
        return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
      }
    } catch (e) {
      console.error('Date parsing error:', e);
    }
    return null;
  }).filter(d => d !== null);
  
  // Remove duplicate formatted dates only
  const unique = [...new Set(formattedDates)];
  return unique.join(' & ') || '-';
};

const parseAndJoinArray = (field) => {
  if (!field) return '-';
  if (Array.isArray(field)) return joinArray(field);
  if (typeof field === 'string') {
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? joinArray(parsed) : (parsed || '-');
    } catch {
      return field;
    }
  }
  return field;
};

const parseAndJoinUniqueArray = (field) => {
  if (!field) return '-';
  if (Array.isArray(field)) return joinUniqueArray(field);
  if (typeof field === 'string') {
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? joinUniqueArray(parsed) : (parsed || '-');
    } catch {
      return field;
    }
  }
  return field;
};

const getFirstElement = (arr) => {
  if (!arr || !Array.isArray(arr)) return '-';
  return arr[0] || '-';
};

const formatAdminAirRow = (shipment) => ({
    id: shipment.id,
    awbNumber: shipment.awbNumber || shipment.awb || '',
    username: shipment.username || '',
    customerName: shipment.customerName || '',
    sbNumber: joinArray(shipment.sbNumbers),
    sbDate: formatDates(shipment.sbDates),
    portCode: shipment.portCode || '',
    cityCode: shipment.destCode || '',
    destCity: shipment.destCity || '',
    destCountry: shipment.destCountry || '',
    flightNo: parseAndJoinUniqueArray(shipment.flightNumbers),
    flightDate: parseAndJoinUniqueArray(shipment.flightDates),
    totalPkt: shipment.numberOfPackages || '-',
    weight: shipment.grossWeight || '-',
    chWeight: shipment.chargeableWeight || '-',
    amount: parseAndJoinArray(shipment.paymentsAmount),
    activity: parseAndJoinArray(shipment.paymentActivity),
    fob: parseAndJoinArray(shipment.sbFobValues),
    dbk: parseAndJoinArray(shipment.DBK ?? shipment.dbk ?? shipment.dbkClaim) || '-',
    rodtep: parseAndJoinArray(shipment.Rodtep ?? shipment.rodtep ?? shipment.rodtepAmt) || '-',
    rosctl: parseAndJoinArray(shipment.Rosctl ?? shipment.rosctl ?? shipment.rosctlAmt) || '-',
    igst: parseAndJoinArray(shipment.IGST ?? shipment.igst ?? shipment.igstAmt) || '-',
    // The exporter's own invoice, which the Excel prints as "Exp Inv No" and
    // "Exp Inv Date". It was never carried onto the row, so both came out blank.
    invoiceNumber: parseAndJoinArray(shipment.invoiceNumber ?? shipment.invoice_number) || '',
    invoiceDate: parseAndJoinArray(shipment.invoiceDate ?? shipment.invoice_date) || '',
    fcValue: shipment.fcValue || '-',
    fcType: shipment.fcType || '-',
    fcRate: shipment.fcRate || '-',
    buyerName: shipment.buyerName || '-',
    awbPdf: shipment.awbPdf || shipment.awb || '',
    awb: shipment.awbPdf || shipment.awb || '',
    leo: shipment.leoPdf || shipment.leo || '',
    inv: shipment.invPdf || shipment.inv || '',
    invoice: shipment.invoicePdf || shipment.invoice || '',
    pur: shipment.purPdf || shipment.pur || '',
    service: shipment.shipmentType || '',
    status: shipment.leoStatus || shipment.status || 'Pending',
    editedBy: shipment.editedBy || shipment.edited_by || '-'
});

const formatAdminSeaRow = (s) => ({
  id: s.id,
  portCode: s.port_code || s.portCode || '-',
  sbNo: s.sb_no || s.sbNo || '-',
  sbDate: s.sb_date || s.sbDate || '-',
  pkg: s.pkg || '-',
  gwt: s.gwt || '-',
  portOfLoading: s.port_of_loading || s.portOfLoading || '-',
  stateOfOrigin: s.state_of_origin || s.stateOfOrigin || '-',
  portOfDischargeCode: s.port_of_discharge_code || s.portOfDischargeCode || '-',
  portOfDischarge: s.port_of_discharge || s.portOfDischarge || '-',
  portOfFinalDestinationCode: s.port_of_final_destination_code || s.portOfFinalDestinationCode || '-',
  portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
  countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
  exporterName: s.exporter_name || s.exporterName || '-',
  buyerName: s.buyer_name || s.buyerName || '-',
  consigneeName: s.consignee_name || s.consigneeName || '-',
  mode: s.mode || '-',
  lut: s.lut || '-',
  fobValue: s.fob_value || s.fobValue || '-',
  dbkClaim: s.dbk_claim || s.dbkClaim || '-',
  igstAmt: s.igst_amt || s.igstAmt || '-',
  cessAmt: s.cess_amt || s.cessAmt || '-',
  igstValue: s.igst_value || s.igstValue || '-',
  rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-',
  rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
  invNo: s.inv_no || s.invNo || '-',
  invAmt: s.inv_amt || s.invAmt || '-',
  currency: s.currency || '-',
  leoDate: s.leo_date || s.leoDate || '-',
  invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
  blNumber: s.bl_number || s.blNumber || '-',
  containerNo: s.container_no || s.containerNo || '-',
  containerQty: s.container_qty || s.containerQty || '-',
  leoPdf: s.leo_pdf || s.leoPdf || '',
  blPdf: s.bl_pdf || s.blPdf || '',
  invPlPdf: s.inv_pl_pdf || s.invPlPdf || '',
  invoicePdf: s.invoice_pdf || s.invoicePdf || '',
  purPdf: s.pur_pdf || s.purPdf || '',
  editedBy: s.edited_by || s.editedBy || '-',
  createdAt: s.created_at || s.createdAt || new Date().toISOString(),
  type: 'Sea-Export'
});

const AdminDashboard = ({ allUsers, setAllUsers }) => {
  const navigate = useNavigate();
  const { getToken, fetchUsers, allUsers: globalUsers, user: currentUser } = useAuth();
  
  // CSV Export function
  const exportToCSV = (data, filename = 'shipments.csv') => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }
    const formatAwbForCsv = (awb) => {
      if (awb === null || awb === undefined || awb === '') return '';
      const str = String(awb);
      return str.startsWith('0') ? `="${str}"` : str;
    };


    const headers = ['AWB Number', 'Username', 'Customer Name', 'SB Number', 'SB Date', 'City Code', 'Dest City', 'Dest Country', 'Flight No', 'Flight Date', 'Total Pkt', 'Weight', 'CH Weight', 'Amount', 'Activity', 'FOB', 'DBK', 'Rodtep', 'Rosctl', 'IGST', 'FC Value', 'FC Type', 'FC Rate', 'Buyer Name', 'AWB', 'LEO', 'INV/PL', 'INVOICE', 'PUR'];
    const csvRows = data.map(shipment => [
      formatAwbForCsv(shipment.awbNumber || ''),
      shipment.username || '',
      shipment.customerName || '',
      shipment.sbNumber || '',
      shipment.sbDate || '',
      shipment.cityCode || '',
      shipment.destCity || '',
      shipment.destCountry || '',
      shipment.flightNo || '',
      shipment.flightDate || '',
      shipment.totalPkt || '',
      shipment.weight || '',
      shipment.chWeight || '',
      shipment.amount || '',
      shipment.activity || '',
      shipment.fob || '',
      shipment.dbk || '',
      shipment.rodtep || '',
      shipment.rosctl || '',
      shipment.igst || '',
      shipment.fcValue || '',
      shipment.fcType || '',
      shipment.fcRate || '',
      shipment.buyerName || '',
      shipment.awb ? 'Yes' : 'No',
      shipment.leo ? 'Yes' : 'No',
      shipment.invpl ? 'Yes' : 'No',
      shipment.invoice ? 'Yes' : 'No',
      shipment.pur ? 'Yes' : 'No'
    ]);
    const csvContent = [headers.join(','), ...csvRows.map(row => row.map(escapeCsv).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const [activeTab, setActiveTab] = useState('users');
  // The server counts rows as it stores them; the table shows fewer, because
  // the ones awaiting approval are dropped on arrival. Paging off the rendered
  // count asks from the wrong place and the pages drift.
  const airRawCount = useRef(0);
  const [shipBook, setShipBook] = useState(null);  // every consignment, for searching and filtering past the loaded page
  const [consoleSearch, setConsoleSearch] = useState('');   // the users tab
  const [hsnSearch, setHsnSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [shipmentType, setShipmentType] = useState('Air-Export');
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  // Lightweight, independent of PendingApprovalQueue mounting — so the tab badge
  // shows a count immediately on load, not only after the user opens that view.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall(`${API_ENDPOINTS.GET_ALL_SHIPMENTS}?approvalStatus=PENDING_APPROVAL&limit=1&offset=0`, { method: 'GET' });
        const result = await res.json();
        if (res.ok && result.success) setPendingApprovalCount(result.data?.totalRecords || 0);
      } catch {
        // badge count is a convenience — fail silently
      }
    })();
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [searchAWB, setSearchAWB] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  // Invoice / payment filters — the same set the Accountant dashboard offers.
  const [invoiceMap, setInvoiceMap] = useState({}); // { awbNumber|blNumber: [invoice, ...] }
  const [allInvoicesList, setAllInvoicesList] = useState([]);
  // Editing an invoice raised straight against a client, from Client Master.
  const [customerInvoiceEdit, setCustomerInvoiceEdit] = useState(null);
  const [customerInvoiceListOpen, setCustomerInvoiceListOpen] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('all'); // all | no_invoice | unpaid | partially_paid | paid
  const [unpaidSince, setUnpaidSince] = useState(null);
  const [showUnpaidSincePicker, setShowUnpaidSincePicker] = useState(false);
  const [overdueDays, setOverdueDays] = useState('');
  const [customers, setCustomers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [seaExportShipments, setSeaExportShipments] = useState([]);
  const [loadingSeaExport, setLoadingSeaExport] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const [errors, setErrors] = useState({});
  const [editUser, setEditUser] = useState({
    customerName: '',
    email: '',
    phoneNumber: '',
    username: '',
    password: '',
    role: 'employee',
    status: 'active',
    website: ''
  });


  // Insights state
  const [insightsData, setInsightsData] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [monthWiseData, setMonthWiseData] = useState([]);
  const [customerWiseData, setCustomerWiseData] = useState([]);
  const [productWiseData, setProductWiseData] = useState([]);
  const [userPerformanceData, setUserPerformanceData] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [avgOrderValue, setAvgOrderValue] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Drill-down state
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [insightCustomer, setInsightCustomer] = useState(null);
  const [drillDownCustomerData, setDrillDownCustomerData] = useState([]);
  const [drillDownProductData, setDrillDownProductData] = useState([]);
  const [topCustomer, setTopCustomer] = useState({ name: '-', amount: 0 });
  const [topProduct, setTopProduct] = useState({ name: '-', amount: 0 });
  const [topCity, setTopCity] = useState({ name: '-', amount: 0 });

  // Pagination state
  const PAGE_SIZE = 20;
  const [airHasMore, setAirHasMore] = useState(true);
  const [airLoadingMore, setAirLoadingMore] = useState(false);
  const [seaHasMore, setSeaHasMore] = useState(true);
  const [seaLoadingMore, setSeaLoadingMore] = useState(false);
  const fetchShipmentsRef = useRef(null);
  const airSentinelRef = useRef(null);
  const seaSentinelRef = useRef(null);

  // Chart colors
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];
  const GRADIENT_COLORS = [
    { start: '#3b82f6', end: '#1d4ed8' },
    { start: '#10b981', end: '#059669' },
    { start: '#f59e0b', end: '#d97706' },
    { start: '#ef4444', end: '#dc2626' },
    { start: '#8b5cf6', end: '#7c3aed' },
  ];

  const getDisplayRole = (role) => {
    return role;
  };

  const normalizePhoneNumber = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length > 10) {
      return digits.startsWith('91') ? digits.slice(-10) : digits.slice(-10);
    }
    return digits;
  };

  // Admin can only manage these 3 roles
  const ADMIN_ALLOWED_ROLES = ['user', 'accountant', 'employee'];

  // Fetch customers for shipments filter
  useEffect(() => {
    const fetchCustomersList = async () => {
      try {
        const response = await apiCall(API_ENDPOINTS.GET_CUSTOMERS, {
          method: 'GET'
        });

        const data = await response.json();

        if (response.ok && response.status === 200) {
          console.log('Admin Customers API Response:', data);
          console.log('Raw data array:', data.data);
          
          // data is directly an array
          let rawCustomers = data.data || [];
          
          // If rawCustomers is not an array, make it empty
          if (!Array.isArray(rawCustomers)) {
            console.log('rawCustomers is not array:', rawCustomers);
            rawCustomers = [];
          }
          
          const customersList = rawCustomers.map(c => ({
            id: c.id,
            name: c.our_users || c.custName || c.name || c.username || 'Unknown',
            email: c.email || ''
          }));
          console.log('Admin Mapped customers:', customersList);
          setCustomers(customersList);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };

    fetchCustomersList();
  }, []);


  // Invoice records per AWB / BL — drives the payment columns in the table and
  // the payment filters above it. One call for the lot: asking per customer
  // missed any shipment whose user isn't in this admin's customer list, and
  // its row then still offered "Generate" though an invoice existed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all([(async () => {
          const [airData, seaData] = await Promise.all([
            apiCall(API_ENDPOINTS.INVOICE_SUBMIT, { method: 'GET' })
              .then(r => (r.ok ? r.json() : null)).catch(() => null),
            apiCall(API_ENDPOINTS.INVOICE_SUBMIT_SEA, { method: 'GET' })
              .then(r => (r.ok ? r.json() : null)).catch(() => null),
          ]);
          return { airData, seaData };
        })()]);
        if (cancelled) return;
        const map = {};
        const flat = [];
        results.forEach(({ airData, seaData }) => {
          [airData, seaData].forEach((data) => {
            if (!data) return;
            const invoices = Array.isArray(data) ? data : (data.data || data.invoices || []);
            flat.push(...invoices);
            invoices.forEach((inv) => {
              const shipmentNo = inv.awbNumber || inv.awb_number || inv.blNumber || inv.bl_number;
              if (!shipmentNo) return;
              if (!map[shipmentNo]) map[shipmentNo] = [];
              map[shipmentNo].push(inv);
            });
          });
        });
        setInvoiceMap(map);
        setAllInvoicesList(flat);
      } catch (err) {
        console.error('Error building invoice map:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all shipments
  useEffect(() => {
    const fetchAllShipments = async (airOffset = 0, seaOffset = 0, append = false) => {
      // Fetch Air Export shipments
      if (!append) setShipmentsLoading(true);
      else setAirLoadingMore(true);
      try {
        const response = await apiCall(`${API_ENDPOINTS.GET_ALL_SHIPMENTS}?limit=${PAGE_SIZE}&offset=${airOffset}`, {
          method: 'GET'
        });

        const data = await response.json();

        if (response.ok && response.status === 200) {
          const rawShipmentsArray = data.data?.shipments || data.shipments || data.data || [];
          // Shipments created by Gmail automation sit in PENDING_APPROVAL/REJECTED until
          // a human approves them via the Pending Approval tab — keep them out of the
          // main list until then (manually-created records have approvalStatus === null).
          const shipmentsArray = Array.isArray(rawShipmentsArray)
            ? rawShipmentsArray.filter(s => s.approvalStatus !== 'PENDING_APPROVAL' && s.approvalStatus !== 'REJECTED')
            : rawShipmentsArray;

          if (Array.isArray(shipmentsArray)) {
            const formattedShipments = shipmentsArray.map(formatAdminAirRow);
            if (append) {
              setShipments(prev => {
                const existing = new Set(prev.map(s => s.awbNumber));
                return [...prev, ...formattedShipments.filter(s => !existing.has(s.awbNumber))];
              });
            } else {
              setShipments(formattedShipments);
            }
            airRawCount.current = append
              ? airRawCount.current + rawShipmentsArray.length
              : rawShipmentsArray.length;
            setAirHasMore(rawShipmentsArray.length === PAGE_SIZE);
          } else if (!append) {
            setShipments([]);
            setAirHasMore(false);
          }
        }
      } catch (error) {
        console.error('Error fetching shipments:', error);
        if (!append) { setShipments([]); setAirHasMore(false); }
      } finally {
        if (!append) setShipmentsLoading(false);
        else setAirLoadingMore(false);
      }

      if (!append) {
      // Fetch Sea Export shipments
      setLoadingSeaExport(true);
      try {
        const response = await apiCall(`${API_ENDPOINTS.GET_SEA_EXPORT}?limit=${PAGE_SIZE}&offset=${seaOffset}`, {
          method: 'GET'
        });

        const data = await response.json();
        console.log('Sea Export API Response (Admin):', data);

        if (response.ok && data.success) {
          const formattedShipments = (data.data || []).map(formatAdminSeaRow);
          setSeaExportShipments(formattedShipments);
          setSeaHasMore(formattedShipments.length === PAGE_SIZE);
        }
      } catch (error) {
        console.error('Error fetching sea export shipments:', error);
      } finally {
        setLoadingSeaExport(false);
      }
      } // end if (!append)
    };

    fetchShipmentsRef.current = fetchAllShipments;

    if (activeTab === 'shipments') {
      fetchAllShipments();
    } else if (activeTab === 'users' && globalUsers.length === 0 && allUsers.length === 0) {
      fetchUsers();
    }
  }, [activeTab, globalUsers.length, allUsers.length]);

  // A filter has to search the whole book — the match may sit on a page the
  // table has not reached yet — and while one is on the list is the book, so
  // scrolling must not keep appending pages to it.
  const anyShipFilter = !!(selectedCustomer || searchAWB.trim() || dateRange
    || paymentFilter !== 'all' || unpaidSince || overdueDays !== '');

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const airObs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && airHasMore && !airLoadingMore && !anyShipFilter) {
        setAirLoadingMore(true);
        if (fetchShipmentsRef.current) {
          fetchShipmentsRef.current(airRawCount.current, 0, true).finally(() => setAirLoadingMore(false));
        }
      }
    }, { threshold: 0.1 });
    if (airSentinelRef.current) airObs.observe(airSentinelRef.current);
    return () => airObs.disconnect();
  }, [airHasMore, airLoadingMore, shipments.length, anyShipFilter]);

  useEffect(() => {
    const seaObs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && seaHasMore && !seaLoadingMore && !anyShipFilter) {
        setSeaLoadingMore(true);
        const offset = seaExportShipments.length;
        apiCall(`${API_ENDPOINTS.GET_SEA_EXPORT}?limit=${PAGE_SIZE}&offset=${offset}`, { method: 'GET' })
          .then(r => r.json())
          .then(data => {
            if (data.success) {
              const more = (data.data || []).map(s => ({
                id: s.id, portCode: s.port_code || s.portCode || '-', sbNo: s.sb_no || s.sbNo || '-',
                sbDate: s.sb_date || s.sbDate || '-', pkg: s.pkg || '-', gwt: s.gwt || '-',
                portOfLoading: s.port_of_loading || s.portOfLoading || '-',
                stateOfOrigin: s.state_of_origin || s.stateOfOrigin || '-',
                portOfDischargeCode: s.port_of_discharge_code || s.portOfDischargeCode || '-',
                portOfDischarge: s.port_of_discharge || s.portOfDischarge || '-',
                portOfFinalDestinationCode: s.port_of_final_destination_code || s.portOfFinalDestinationCode || '-',
                portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
                countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
                exporterName: s.exporter_name || s.exporterName || '-',
                buyerName: s.buyer_name || s.buyerName || '-',
                consigneeName: s.consignee_name || s.consigneeName || '-',
                mode: s.mode || '-', lut: s.lut || '-', fobValue: s.fob_value || s.fobValue || '-',
                dbkClaim: s.dbk_claim || s.dbkClaim || '-', igstAmt: s.igst_amt || s.igstAmt || '-',
                cessAmt: s.cess_amt || s.cessAmt || '-', igstValue: s.igst_value || s.igstValue || '-',
                rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-', rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
                invNo: s.inv_no || s.invNo || '-', invAmt: s.inv_amt || s.invAmt || '-',
                currency: s.currency || '-', leoDate: s.leo_date || s.leoDate || '-',
                invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
                blNumber: s.bl_number || s.blNumber || '-', containerNo: s.container_no || s.containerNo || '-',
                containerQty: s.container_qty || s.containerQty || '-',
                leoPdf: s.leo_pdf || s.leoPdf || '', blPdf: s.bl_pdf || s.blPdf || '',
                invPlPdf: s.inv_pl_pdf || s.invPlPdf || '', invoicePdf: s.invoice_pdf || s.invoicePdf || '',
                purPdf: s.pur_pdf || s.purPdf || '', editedBy: s.edited_by || s.editedBy || '-',
                createdAt: s.created_at || s.createdAt || new Date().toISOString(), type: 'Sea-Export'
              }));
              setSeaExportShipments(prev => {
                const existing = new Set(prev.map(x => x.blNumber));
                return [...prev, ...more.filter(x => !existing.has(x.blNumber))];
              });
              setSeaHasMore(more.length === PAGE_SIZE);
            }
          })
          .catch(() => {})
          .finally(() => setSeaLoadingMore(false));
      }
    }, { threshold: 0.1 });
    if (seaSentinelRef.current) seaObs.observe(seaSentinelRef.current);
    return () => seaObs.disconnect();
  }, [seaHasMore, seaLoadingMore, seaExportShipments.length, anyShipFilter]);

  const handleRefreshUsers = async () => {
    setLoading(true);
    setError('');
    await fetchUsers();
    setLoading(false);
  };

  const generateStrongPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setEditUser({ ...editUser, password });
    setErrors({ ...errors, password: '' });
  };

  const validateForm = (user, isEdit = false) => {
    const newErrors = {};
    const usersToCheck = globalUsers.length > 0 ? globalUsers : allUsers;
    
    // Name validation
    if (!user.customerName || user.customerName.trim().length < 2) {
      newErrors.customerName = 'Customer name must be at least 2 characters';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!user.email || !emailRegex.test(user.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (isEdit && originalUser) {
      // Only check for duplicates if email has changed
      if (user.email.toLowerCase() !== originalUser.email?.toLowerCase()) {
        const emailExists = usersToCheck.some(u => 
          u.email?.toLowerCase() === user.email.toLowerCase() && u.id !== selectedUser?.id
        );
        if (emailExists) {
          newErrors.email = 'This email is already registered';
        }
      }
    } else if (!isEdit) {
      // For new users, always check
      const emailExists = usersToCheck.some(u => 
        u.email?.toLowerCase() === user.email.toLowerCase()
      );
      if (emailExists) {
        newErrors.email = 'This email is already registered';
      }
    }
    
    // Phone validation
    const phoneRegex = /^[0-9]{10}$/;
    if (!user.phoneNumber || !phoneRegex.test(user.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must be exactly 10 digits';
    } else if (isEdit && originalUser) {
      // Only check for duplicates if phone has changed
      if (user.phoneNumber !== originalUser.phoneNumber) {
        const phoneExists = usersToCheck.some(u => 
          u.mobile === user.phoneNumber && u.id !== selectedUser?.id
        );
        if (phoneExists) {
          newErrors.phoneNumber = 'This phone number is already registered';
        }
      }
    } else if (!isEdit) {
      // For new users, always check
      const phoneExists = usersToCheck.some(u => 
        u.mobile === user.phoneNumber
      );
      if (phoneExists) {
        newErrors.phoneNumber = 'This phone number is already registered';
      }
    }
    
    // Username validation
    if (!user.username || user.username.trim().length < 5) {
      newErrors.username = 'Username must be at least 5 characters';
    } else if (isEdit && originalUser) {
      // Only check for duplicates if username has changed
      if (user.username.toLowerCase() !== originalUser.username?.toLowerCase()) {
        const usernameExists = usersToCheck.some(u => 
          u.username?.toLowerCase() === user.username.toLowerCase() && u.id !== selectedUser?.id
        );
        if (usernameExists) {
          newErrors.username = 'This username is already taken';
        }
      }
    } else if (!isEdit) {
      // For new users, always check
      const usernameExists = usersToCheck.some(u => 
        u.username?.toLowerCase() === user.username.toLowerCase()
      );
      if (usernameExists) {
        newErrors.username = 'This username is already taken';
      }
    }
    
    // Password validation (only for edit if password provided)
    if (isEdit) {
      if (user.password && user.password.length > 0 && user.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    } else {
      if (!user.password || user.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    }
    
    // Website validation (optional)
    if (user.website && user.website.trim()) {
      const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      if (!urlRegex.test(user.website)) {
        newErrors.website = 'Please enter a valid website URL';
      }
    }
    
    return newErrors;
  };

  const handleEditUserClick = (user) => {
    console.log('Edit User Clicked - User object:', user);
    setSelectedUser(user);
    const phoneNum = normalizePhoneNumber(user.mobile || user.phoneNumber);
    const userData = {
      customerName: user.name,
      email: user.email,
      phoneNumber: phoneNum,
      username: user.username,
      password: user.password || '',
      role: user.role,
      status: user.status || 'active',
      website: user.website || ''
    };
    console.log('Edit User - userData:', userData);
    setEditUser(userData);
    // Store original values for comparison
    const originalData = {
      customerName: user.name,
      email: user.email,
      phoneNumber: phoneNum,
      username: user.username,
      role: user.role,
      status: user.status || 'active',
      website: user.website || ''
    };
    console.log('Edit User - originalData:', originalData);
    setOriginalUser(originalData);
    setErrors({});
    setShowPassword(false);
    setShowEditUserModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setErrors({});
    setError('');
    
    console.log('Update User - Current editUser state:', editUser);
    console.log('Update User - Original user state:', originalUser);
    
    const validationErrors = validateForm(editUser, true);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    
    try {
      // Build object with only changed fields
      const changedFields = {};
      
      if (editUser.username !== originalUser.username) {
        changedFields.username = editUser.username;
        console.log('Username changed:', editUser.username);
      }
      if (editUser.email !== originalUser.email) {
        changedFields.email = editUser.email;
        console.log('Email changed:', editUser.email);
      }
      if (editUser.customerName !== originalUser.customerName) {
        changedFields.Name = editUser.customerName;
        console.log('Customer name changed:', editUser.customerName);
      }
      if (editUser.phoneNumber !== originalUser.phoneNumber) {
        changedFields.phNum = `+91${editUser.phoneNumber}`;
        console.log('Phone number changed:', editUser.phoneNumber, ' -> Sending as:', changedFields.phNum);
      }
      if (editUser.role !== originalUser.role) {
        changedFields.role = editUser.role;
        console.log('Role changed:', editUser.role);
      }
      if (editUser.status !== originalUser.status) {
        changedFields.status = editUser.status;
        console.log('Status changed:', editUser.status);
      }
      if (editUser.website !== originalUser.website) {
        changedFields.website = editUser.website || '';
        console.log('Website changed:', editUser.website);
      }
      // Include password if it was changed (not empty)
      if (editUser.password && editUser.password.trim() !== '') {
        changedFields.password = await hashPassword(editUser.password);
        console.log('Password changed');
      }
      
      console.log('Changed fields to send to API:', changedFields);
      
      // If no fields changed, just close modal
      if (Object.keys(changedFields).length === 0) {
        console.log('No fields changed, closing modal');
        closeModals();
        return;
      }
      
      const response = await apiCall(`${API_ENDPOINTS.UPDATE_USER}${selectedUser.id}`, {
        method: 'PATCH',
        body: JSON.stringify(changedFields)
      });

      const data = await response.json();

      if (response.ok && response.status === 200) {
        // Update local state
        const updatedUsers = allUsers.map(user => 
          user.id === selectedUser.id 
            ? { 
                ...user, 
                name: editUser.customerName,
                email: editUser.email,
                mobile: editUser.phoneNumber,
                username: editUser.username,
                password: editUser.password || user.password,
                role: editUser.role,
                status: editUser.status,
                website: editUser.website || null
              }
            : user
        );
        setAllUsers(updatedUsers);
        closeModals();
      } else {
        setError(data.message || 'Failed to update user');
      }
    } catch (err) {
      console.error('Update user error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiCall(`${API_ENDPOINTS.DELETE_USER}${selectedUser.id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok && response.status === 200) {
        // Remove user from local state
        const updatedUsers = allUsers.filter(user => user.id !== selectedUser.id);
        setAllUsers(updatedUsers);
        setShowDeleteConfirm(false);
        setSelectedUser(null);
      } else {
        setError(data.message || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Delete user error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const closeModals = () => {
    setShowEditUserModal(false);
    setShowDeleteConfirm(false);
    setSelectedUser(null);
    setOriginalUser(null);
    setErrors({});
    setShowPassword(false);
    setEditUser({
      customerName: '',
      email: '',
      phoneNumber: '',
      username: '',
      password: '',
      role: 'employee',
      status: 'active',
      website: ''
    });
  };

  // Filter users - Admin should only see users they can manage
  const filteredUsers = allUsers.filter(user => ADMIN_ALLOWED_ROLES.includes(user.role));

  const normalizeValue = (value) => String(value || '').trim().toLowerCase();

  const getAllTimestamps = (value) => {
    if (!value) return [];

    const normalizeValues = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') {
        const trimmed = val.trim();
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed;
        } catch {
          // ignore
        }
        if (trimmed.includes(' & ')) return trimmed.split(' & ');
        if (trimmed.includes(',')) return trimmed.split(',');
        return [trimmed];
      }
      return [val];
    };

    const toTimestamp = (val) => {
      if (!val) return 0;
      const str = String(val).trim();

      const ddmmyyyy = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (ddmmyyyy) {
        const [, dd, mm, yyyy] = ddmmyyyy;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const ddmmyyDash = str.match(/^(\d{2})-(\d{2})-(\d{2,4})$/);
      if (ddmmyyDash) {
        let [, dd, mm, yy] = ddmmyyDash;
        let yyyy = parseInt(yy, 10);
        if (yyyy < 100) yyyy += 2000;
        return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const ddmmmyy = str.match(/^(\d{2})-(\w{3})-(\d{2,4})$/);
      if (ddmmmyy) {
        const monthMap = {
          JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
          JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12'
        };
        const dd = ddmmmyy[1];
        const mmm = ddmmmyy[2].toUpperCase();
        let yyyy = parseInt(ddmmmyy[3], 10);
        if (yyyy < 100) yyyy += 2000;
        const mm = monthMap[mmm];
        if (mm) return new Date(`${yyyy}-${mm}-${dd}`).getTime() || 0;
      }

      const parsed = new Date(str).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    return normalizeValues(value).map(toTimestamp).filter((ts) => ts > 0);
  };

  const inSelectedDateRange = (value) => {
    if (!dateRange || !dateRange[0]?.startDate || !dateRange[0]?.endDate) return true;
    const startDate = new Date(dateRange[0].startDate);
    const endDate = new Date(dateRange[0].endDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    const startTs = startDate.getTime();
    const endTs = endDate.getTime();
    const timestamps = getAllTimestamps(value);
    if (!timestamps.length) return false;
    return timestamps.some((ts) => ts >= startTs && ts <= endTs);
  };

  const formatDateRange = () => {
    if (!dateRange || !dateRange[0]) return 'Select date range';
    const start = dateRange[0].startDate;
    const end = dateRange[0].endDate;
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const customerMatches = (values = []) => {
    if (!selectedCustomer) return true;
    const selected = normalizeValue(selectedCustomer);
    return values.some((value) => normalizeValue(value) === selected);
  };

  // The table pages in as you scroll, so a filter could only ever match what
  // had been loaded — search an AWB from page four and it found nothing. The
  // whole book is read once when the tab opens, and the filters read it
  // instead whenever one is on.
  useEffect(() => {
    if (activeTab !== 'shipments' || shipBook) return undefined;
    let cancelled = false;

    (async () => {
      const [airRes, seaRes] = await Promise.all([
        apiCall(API_ENDPOINTS.GET_ALL_SHIPMENTS, { method: 'GET' }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
        apiCall(API_ENDPOINTS.GET_SEA_EXPORT, { method: 'GET' }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]);
      if (cancelled) return;

      const airRaw = (airRes?.data?.shipments || airRes?.shipments || airRes?.data || [])
        .filter((x) => x.approvalStatus !== 'PENDING_APPROVAL' && x.approvalStatus !== 'REJECTED');
      const seaRaw = seaRes?.data || seaRes?.shipments || [];

      // Mapped the way the table maps its page, so one shape serves both.
      setShipBook({
        air: Array.isArray(airRaw) ? airRaw.map(formatAdminAirRow) : [],
        sea: Array.isArray(seaRaw) ? seaRaw.map(formatAdminSeaRow) : [],
      });
    })();

    return () => { cancelled = true; };
  }, [activeTab, shipBook]);

  const matchesPaymentFilter = makePaymentMatcher({ invoiceMap, paymentFilter, unpaidSince, overdueDays });

  const airSource = anyShipFilter ? (shipBook?.air || shipments) : shipments;
  const seaSource = anyShipFilter ? (shipBook?.sea || seaExportShipments) : seaExportShipments;

  const filteredAirShipments = airSource.filter((s) =>
    customerMatches([s.customerName, s.username])
  )
    .filter((s) => matchesShipmentSearch(s, searchAWB))
    .filter((s) => inSelectedDateRange(s.sbDate || s.createdAt || s.updatedAt))
    .filter((s) => matchesPaymentFilter(s.awbNumber, !!s.invoice));

  const filteredSeaExportShipments = seaSource.filter((s) =>
    customerMatches([s.exporterName, s.customerName, s.username])
  )
    .filter((s) => matchesShipmentSearch(s, searchAWB))
    .filter((s) => inSelectedDateRange(s.sbDate || s.createdAt || s.leoDate))
    .filter((s) => matchesPaymentFilter(s.blNumber || s.awbNumber, !!s.invoice));

  const shipmentScopeLabel = selectedCustomer || 'All Users';
  const shipmentHeading = selectedCustomer ? selectedCustomer : 'All Users Shipments';
  const totalVisibleShipments = shipmentType === 'Air-Export'
    ? filteredAirShipments.length
    : filteredSeaExportShipments.length;

  const filteredSeaShipments = filteredSeaExportShipments;
  const shipmentHeadingText = shipmentHeading;
  const shipmentCountText = totalVisibleShipments;

  // Fetch insights data
  const fetchInsightsData = async () => {
    setInsightsLoading(true);
    try {
      // Fetch invoices first (required)
      const invoiceResponse = await apiCall(API_ENDPOINTS.INVOICE_SUBMIT, { method: 'GET' });
      const invoiceData = await invoiceResponse.json();
      
      if (!invoiceResponse.ok) {
        throw new Error('Failed to fetch invoices');
      }
      
      const invoicesArr = Array.isArray(invoiceData) ? invoiceData : (invoiceData.data || invoiceData.invoices || []);
      
      // Filter invoices to only include admin's customers
      // Check both userId and user_id (API might return snake_case)
      const customerIds = customers.map(c => c.id);
      const filteredInvoices = invoicesArr.filter(invoice => {
        const invUserId = invoice.userId || invoice.user_id;
        return customerIds.includes(invUserId);
      });
      
      console.log('Insights API - Raw invoice response:', invoiceData);
      console.log('Insights API - All invoices count:', invoicesArr.length);
      console.log('Insights API - Admin customer IDs:', customerIds);
      console.log('Insights API - Filtered invoices for admin:', filteredInvoices.length);
      if (filteredInvoices.length > 0) {
        console.log('Insights API - First filtered invoice:', filteredInvoices[0]);
      }
      
      // Try to fetch shipments for weight data (optional - don't fail if this errors)
      let shipmentWeightMap = {};
      try {
        const shipmentsResponse = await apiCall(API_ENDPOINTS.GET_ALL_SHIPMENTS, { method: 'GET' });
        if (shipmentsResponse.ok) {
          const shipmentsData = await shipmentsResponse.json();
          // Handle nested data structure: data.shipments
          let shipmentsArr = [];
          if (Array.isArray(shipmentsData)) {
            shipmentsArr = shipmentsData;
          } else if (shipmentsData.data?.shipments) {
            shipmentsArr = shipmentsData.data.shipments;
          } else if (shipmentsData.data) {
            shipmentsArr = Array.isArray(shipmentsData.data) ? shipmentsData.data : [];
          } else if (shipmentsData.shipments) {
            shipmentsArr = shipmentsData.shipments;
          }
          
          shipmentsArr.forEach(shipment => {
            const awb = shipment.awbNumber || shipment.awb_number;
            if (awb) {
              // Use grossWeight from shipment data - remove commas before parsing
              const weightStr = String(shipment.grossWeight || shipment.gross_weight || shipment.chargeableWeight || shipment.chargeable_weight || '0');
              const weight = parseFloat(weightStr.replace(/,/g, '')) || 0;
              shipmentWeightMap[awb] = weight;
            }
          });
        }
      } catch (shipmentError) {
        console.warn('Could not fetch shipment weights:', shipmentError);
        // Continue without weight data
      }
      
      setInsightsData(filteredInvoices);
      setTotalInvoices(filteredInvoices.length);
      
      // Process data for charts with shipment weight map
      processInsightsData(filteredInvoices, shipmentWeightMap);
    } catch (error) {
      console.error('Error fetching insights:', error);
      toast.error('Failed to load insights data');
    } finally {
      setInsightsLoading(false);
    }
  };

  // Extract city from address
  const extractCity = (address) => {
    if (!address) return 'Unknown';
    // Try to extract city from common patterns
    const parts = address.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      // Usually city is second-to-last or contains recognizable city names
      const cityPart = parts[parts.length - 2] || parts[1] || parts[0];
      return cityPart.replace(/\d+/g, '').trim().toUpperCase() || 'Unknown';
    }
    return parts[0]?.toUpperCase() || 'Unknown';
  };

  // Process insights data for charts
  const processInsightsData = (invoicesArr, shipmentWeightMap = {}) => {
    // Month-wise data
    const monthMap = {};
    // Customer-wise data
    const customerMap = {};
    // Product-wise data
    const productMap = {};
    // City-wise data
    const cityMap = {};
    // User performance data
    const userMap = {};
    let total = 0;
    let productCount = 0;

    invoicesArr.forEach(invoice => {
      // Get invoice date from invoiceData array or createdAt
      let invoiceDate = invoice.createdAt;
      if (invoice.invoiceData && Array.isArray(invoice.invoiceData) && invoice.invoiceData[0]) {
        invoiceDate = invoice.invoiceData[0].invoiceDate || invoiceDate;
      }
      
      if (invoiceDate) {
        const date = new Date(invoiceDate);
        const monthYear = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
        
        // Get grand total from summary
        const invoiceTotal = parseFloat(invoice.summary?.grand_total || 0);
        
        // Get customer name from buyer
        const customerName = invoice.buyer?.name || 'Unknown';
        
        // Get city from buyer address
        const city = extractCity(invoice.buyer?.address);
        
        // Get actual weight from shipment using awbNumber
        const awbNumber = invoice.awbNumber || invoice.awb_number || '';
        const shipmentWeight = shipmentWeightMap[awbNumber] || 0;
        
        const enrichedInvoice = { ...invoice, shipmentWeight };

        // Count products from items
        const items = invoice.items || [];
        items.forEach(item => {
          productCount++;
        });
        
        // Month-wise with growth calculation
        if (!monthMap[monthYear]) {
          monthMap[monthYear] = { total: 0, invoices: [], count: 0, weight: 0 };
        }
        monthMap[monthYear].total += invoiceTotal;
        monthMap[monthYear].count += 1;
        monthMap[monthYear].weight += shipmentWeight;
        monthMap[monthYear].invoices.push(enrichedInvoice);
        
        total += invoiceTotal;
        
        // Customer/User performance
        if (!userMap[customerName]) {
          userMap[customerName] = { total: 0, shipments: 0, weight: 0, invoices: [] };
        }
        userMap[customerName].total += invoiceTotal;
        userMap[customerName].shipments += 1;
        userMap[customerName].weight += shipmentWeight;
        userMap[customerName].invoices.push(enrichedInvoice);
        
        // Customer-wise
        if (!customerMap[customerName]) {
          customerMap[customerName] = { total: 0, invoices: [], weight: 0 };
        }
        customerMap[customerName].total += invoiceTotal;
        customerMap[customerName].weight += shipmentWeight;
        customerMap[customerName].invoices.push(enrichedInvoice);
        
        // City-wise
        if (!cityMap[city]) {
          cityMap[city] = { total: 0, invoices: [] };
        }
        cityMap[city].total += invoiceTotal;
        cityMap[city].invoices.push(enrichedInvoice);
        
        // Product-wise from items array
        items.forEach(item => {
          const desc = item.description || 'Other';
          const itemTotal = parseFloat(item.total || 0);
          const serviceLabel = getServices().find(s => s.value === desc)?.label || desc.toUpperCase();
          productMap[serviceLabel] = (productMap[serviceLabel] || 0) + itemTotal;
        });
      }
    });

    setTotalRevenue(total);
    setTotalProducts(productCount);
    setAvgOrderValue(invoicesArr.length > 0 ? Math.round(total / invoicesArr.length) : 0);

    // Convert to arrays for charts with growth calculation
    const monthData = Object.entries(monthMap)
      .map(([month, data]) => ({ 
        month, 
        revenue: Math.round(data.total), 
        invoices: data.invoices,
        count: data.count,
        weight: Math.round((data.weight || 0) * 10) / 10
      }))
      .sort((a, b) => {
        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const [aMonth, aYear] = a.month.split(' ');
        const [bMonth, bYear] = b.month.split(' ');
        if (aYear !== bYear) return parseInt(aYear) - parseInt(bYear);
        return monthOrder.indexOf(aMonth) - monthOrder.indexOf(bMonth);
      });
    
    // Add growth percentage
    monthData.forEach((item, index) => {
      if (index === 0) {
        item.growth = 0;
      } else {
        const prevRevenue = monthData[index - 1].revenue;
        item.growth = prevRevenue > 0 ? Math.round(((item.revenue - prevRevenue) / prevRevenue) * 100) : 0;
      }
    });
    
    const customerData = Object.entries(customerMap)
      .map(([name, data]) => ({ 
        name: name.length > 18 ? name.substring(0, 18) + '...' : name, 
        fullName: name, 
        revenue: Math.round(data.total),
        invoices: data.invoices,
        weight: Math.round((data.weight || 0) * 10) / 10
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
    
    const productData = Object.entries(productMap)
      .map(([name, amount]) => ({ 
        name: name.length > 20 ? name.substring(0, 20) + '...' : name, 
        fullName: name, 
        value: Math.round(amount) 
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
    
    // User performance data
    const userData = Object.entries(userMap)
      .map(([name, data]) => ({
        name: name,
        displayName: name.length > 25 ? name.substring(0, 25) + '...' : name,
        shipments: data.shipments,
        weight: Math.round(data.weight * 10) / 10,
        sales: Math.round(data.total)
      }))
      .sort((a, b) => b.sales - a.sales);

    // Set top performers
    if (customerData.length > 0) {
      setTopCustomer({ name: customerData[0].fullName, amount: customerData[0].revenue });
    }
    if (productData.length > 0) {
      setTopProduct({ name: productData[0].fullName, amount: productData[0].value });
    }
    
    const cityData = Object.entries(cityMap)
      .map(([name, data]) => ({ name, revenue: Math.round(data.total), invoices: data.invoices }))
      .sort((a, b) => b.revenue - a.revenue);
    if (cityData.length > 0) {
      setTopCity({ name: cityData[0].name, amount: cityData[0].revenue });
    }

    console.log('Insights Processed - monthData:', monthData);
    console.log('Insights Processed - customerData:', customerData);
    console.log('Insights Processed - productData:', productData);
    console.log('Insights Processed - totalRevenue:', total);

    setMonthWiseData(monthData);
    setCustomerWiseData(customerData);
    setProductWiseData(productData);
    setUserPerformanceData(userData);
  };

  // Handle month click for drill-down
  const handleMonthClick = (data) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const monthData = data.activePayload[0].payload;
      setSelectedMonth(monthData.month);
      setInsightCustomer(null);
      
      // Process customer data for selected month
      const customerMap = {};
      monthData.invoices.forEach(invoice => {
        const customerName = invoice.buyer?.name || 'Unknown';
        const invoiceTotal = parseFloat(invoice.summary?.grand_total || 0);
        const shipmentWeight = Number(invoice.shipmentWeight || 0);
        if (!customerMap[customerName]) {
          customerMap[customerName] = { total: 0, invoices: [], weight: 0 };
        }
        customerMap[customerName].total += invoiceTotal;
        customerMap[customerName].weight += shipmentWeight;
        customerMap[customerName].invoices.push(invoice);
      });
      
      const customerData = Object.entries(customerMap)
        .map(([name, data]) => ({
          name: name.length > 18 ? name.substring(0, 18) + '...' : name,
          fullName: name,
          revenue: Math.round(data.total),
          invoices: data.invoices,
          weight: Math.round((data.weight || 0) * 10) / 10
        }))
        .sort((a, b) => b.revenue - a.revenue);
      
      setDrillDownCustomerData(customerData);
    }
  };

  // Handle customer click for drill-down
  const handleCustomerClick = (data) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const customerData = data.activePayload[0].payload;
      setInsightCustomer(customerData.fullName);
      
      // Process product data for selected customer
      const productMap = {};
      customerData.invoices.forEach(invoice => {
        const items = invoice.items || [];
        items.forEach(item => {
          const desc = item.description || 'Other';
          const itemTotal = parseFloat(item.total || 0);
          const serviceLabel = getServices().find(s => s.value === desc)?.label || desc.toUpperCase();
          productMap[serviceLabel] = (productMap[serviceLabel] || 0) + itemTotal;
        });
      });
      
      const productData = Object.entries(productMap)
        .map(([name, amount]) => ({
          name: name.length > 20 ? name.substring(0, 20) + '...' : name,
          fullName: name,
          value: Math.round(amount)
        }))
        .sort((a, b) => b.value - a.value);
      
      setDrillDownProductData(productData);
    }
  };

  // Reset drill-down
  const resetDrillDown = () => {
    setSelectedMonth(null);
    setInsightCustomer(null);
    setDrillDownCustomerData([]);
    setDrillDownProductData([]);
  };

  // Fetch insights when tab changes to insights or when customers are loaded
  useEffect(() => {
    if (activeTab === 'insights' && customers.length > 0) {
      fetchInsightsData();
    }
    // Reset drill-down when leaving insights
    if (activeTab !== 'insights') {
      resetDrillDown();
    }
  }, [activeTab, customers]);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label, isPie }) => {
    if (active && payload && payload.length) {
      const point = payload[0] || {};
      const row = point.payload || {};
      const title = row.fullName || row.displayName || row.name || label || 'Details';
      const amount = Number(
        isPie
          ? (point.value ?? row.value ?? row.revenue ?? row.sales ?? 0)
          : (point.value ?? row.value ?? row.revenue ?? row.sales ?? 0)
      );
      const invoiceCount = Array.isArray(row.invoices)
        ? row.invoices.length
        : Number(row.invoices ?? row.count ?? 0);
      const shipmentCount = Number(row.shipments || 0);
      const weight = Number(row.weight || 0);
      const growth = typeof row.growth === 'number' ? row.growth : null;
      const piePercent = typeof row.percent === 'number'
        ? row.percent * 100
        : (typeof point.percent === 'number' ? point.percent * 100 : null);

      return (
        <div className="bg-slate-900 border border-slate-700 rounded-xl p-3.5 shadow-2xl min-w-[220px]">
          <p className="text-slate-200 text-sm font-semibold mb-2 border-b border-slate-700 pb-2">{title}</p>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-cyan-400 text-xs">Value</span>
              <span className="text-white text-sm font-bold">₹{amount.toLocaleString('en-IN')}</span>
            </div>

            {invoiceCount > 0 && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-amber-400 text-xs">Invoices</span>
                <span className="text-white text-sm font-semibold">{invoiceCount}</span>
              </div>
            )}

            {shipmentCount > 0 && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-emerald-400 text-xs">Shipments</span>
                <span className="text-white text-sm font-semibold">{shipmentCount}</span>
              </div>
            )}

            {weight > 0 && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-purple-400 text-xs">Weight</span>
                <span className="text-white text-sm font-semibold">{weight.toLocaleString('en-IN')} kg</span>
              </div>
            )}

            {growth !== null && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sky-400 text-xs">Growth</span>
                <span className={`text-sm font-semibold ${growth >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {growth >= 0 ? '+' : ''}{growth}%
                </span>
              </div>
            )}

            {piePercent !== null && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-pink-400 text-xs">Share</span>
                <span className="text-white text-sm font-semibold">{piePercent.toFixed(1)}%</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };


  // A customer invoice carries no shipment, so it opens on its own page.
  if (customerInvoiceEdit) {
    return (
      <InvoiceGenerationPage
        customerInvoice
        customerUsers={customers}
        existingInvoice={customerInvoiceEdit}
        allInvoices={allInvoicesList}
        onClose={() => setCustomerInvoiceEdit(null)}
      />
    );
  }

  // The topbar box searches whatever tab is open, writing to the same state
  // that tab's own box uses; a tab with nothing to search gets no box.
  const tabSearch = {
    users:     { value: consoleSearch, set: setConsoleSearch, hint: 'Search user, email, phone or ID…' },
    shipments: { value: searchAWB,     set: setSearchAWB,
                 hint: shipmentType === 'Air-Export'
                   ? 'Search AWB, S/B, user, consignee…'
                   : 'Search B/L, S/B, exporter, port…' },
    invoices:  { value: clientSearch,  set: setClientSearch, hint: 'Search client, GSTIN or address…' },
    hsn:       { value: hsnSearch,     set: setHsnSearch,    hint: 'Search HSC code or service…' },
  }[activeTab] || null;

  const navSections = [
    { title: 'Admin core', items: [
      { id: 'users',      label: 'Users Management', icon: Users },
      { id: 'shipments',  label: 'All Shipments',    icon: Package },
      { id: 'pendingApproval', label: 'Pending Approval', icon: Inbox, count: pendingApprovalCount },
      { id: 'invoices',   label: 'Client Master',    icon: FileText },
      { id: 'hsn',        label: 'HSC Codes',        icon: Hash },
    ] },
    { title: 'Analytics', items: [
      { id: 'insights',   label: 'Sales Insights',   icon: BarChart3 },
    ] },
  ];

  return (
    <AdminShell
      sections={navSections}
      activeId={activeTab}
      onSelect={setActiveTab}
      role="Admin"
      cta={{ label: 'Add New User', icon: UserPlus, id: 'addUser', onClick: () => setActiveTab('addUser') }}
      search={tabSearch?.value ?? ''}
      onSearch={tabSearch?.set}
      searchPlaceholder={tabSearch?.hint}
    >
      <div className="max-w-[1600px] mx-auto">
        {activeTab === 'addUser' && (
          <AddUserPage
            allUsers={allUsers}
            embedded
            onDone={() => { setActiveTab('users'); handleRefreshUsers(); }}
          />
        )}

        {activeTab === 'pendingApproval' && (
          <PendingApprovalQueue
            eyebrow="Admin · Automation"
            onApproved={() => fetchShipmentsRef.current?.(0, 0, false)}
            onCountChange={setPendingApprovalCount}
          />
        )}

        {activeTab === 'users' && (
          <SuperAdminUsers
            scope="admin"
            users={filteredUsers}
            currentUser={currentUser}
            loading={loading}
            error={error}
            search={consoleSearch}
            onRetry={handleRefreshUsers}
            onAdd={() => setActiveTab('addUser')}
            onEdit={handleEditUserClick}
            onDelete={handleDeleteUser}
          />
        )}

        {activeTab === 'hsn' && <HsnCodeManager search={hsnSearch} onSearch={setHsnSearch} />}

        {/* Shipments Section */}
        {activeTab === 'shipments' && (
          <div>
            {(() => {
              const isAir = shipmentType === 'Air-Export';
              const rows = isAir ? filteredAirShipments : filteredSeaShipments;
              const book = isAir ? shipments : seaExportShipments;

              // A shipment counts as billed once an invoice carries its number.
              const billed = (x) => (invoiceMap[x.awbNumber] || invoiceMap[x.blNumber] || []).length > 0;
              const invoiced = book.filter(billed).length;
              const pct = (n) => (book.length ? `${Math.round((n / book.length) * 100)}%` : '—');

              // FOB arrives as a joined display string ("1,000 & 2,000"), so
              // every part is summed rather than parsed as one number.
              const fobOf = (x) => String((isAir ? x.fob : x.fobValue) ?? '')
                .split('&')
                .reduce((sum, part) => sum + (parseFloat(String(part).replace(/[^\d.-]/g, '')) || 0), 0);
              const fob = book.reduce((sum, x) => sum + fobOf(x), 0);
              const shortInr = (n) => (n >= 1e7 ? `₹${(n / 1e7).toFixed(2)} Cr`
                : n >= 1e5 ? `₹${(n / 1e5).toFixed(2)} L`
                : `₹${Math.round(n).toLocaleString('en-IN')}`);

              const facets = [
                ...(selectedCustomer ? [{ key: 'u', label: `User: ${selectedCustomer}`, clear: () => setSelectedCustomer('') }] : []),
                ...(searchAWB.trim() ? [{ key: 'q', label: `Search: ${searchAWB.trim()}`, clear: () => setSearchAWB('') }] : []),
                ...(dateRange ? [{ key: 'd', label: `Dates: ${formatDateRange()}`, clear: () => setDateRange(null) }] : []),
                ...(paymentFilter !== 'all' ? [{ key: 'p', label: `Billing: ${paymentFilter.replace(/_/g, ' ')}`, clear: () => setPaymentFilter('all') }] : []),
                ...(unpaidSince ? [{ key: 's', label: `Unpaid since ${unpaidSince}`, clear: () => setUnpaidSince('') }] : []),
                ...(overdueDays !== '' ? [{ key: 'o', label: `Overdue by ${overdueDays}d`, clear: () => setOverdueDays('') }] : []),
              ];
              const resetAll = () => {
                setSelectedCustomer(''); setSearchAWB(''); setDateRange(null);
                setPaymentFilter('all'); setUnpaidSince(''); setOverdueDays('');
              };

              const miniStats = [
                { id: 'total', label: isAir ? 'Air consignments' : 'Sea consignments', value: book.length,
                  note: `${rows.length} match the filters`,
                  bar: isAir ? 'bg-cyan-500' : 'bg-emerald-500',
                  tone: isAir ? 'text-cyan-600 dark:text-cyan-300' : 'text-emerald-600 dark:text-emerald-300' },
                { id: 'billed', label: 'Invoiced', value: invoiced, note: pct(invoiced),
                  bar: 'bg-blue-500', tone: 'text-blue-600 dark:text-blue-300' },
                { id: 'unbilled', label: 'Awaiting invoice', value: book.length - invoiced, note: pct(book.length - invoiced),
                  bar: 'bg-amber-500', tone: 'text-amber-600 dark:text-amber-300',
                  on: paymentFilter === 'no_invoice',
                  apply: () => setPaymentFilter(paymentFilter === 'no_invoice' ? 'all' : 'no_invoice') },
                { id: 'fob', label: 'FOB value', value: shortInr(fob),
                  note: `across ${book.length} ${isAir ? 'AWBs' : 'B/Ls'}`,
                  bar: 'bg-violet-500', tone: 'text-violet-600 dark:text-violet-300' },
              ];

              const segment = (on) => `px-3.5 h-9 inline-flex items-center gap-1.5 rounded-lg text-[13px] font-bold transition ${
                on ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100'}`;

              return (
                <>
                  <PageHead
                    eyebrow="Admin · Consignments"
                    title="All Shipments"
                    badge={<Pill tone="emerald" dot>{book.length} consignments</Pill>}
                    subtitle={selectedCustomer
                      ? `Consignments raised by ${selectedCustomer}.`
                      : 'Every consignment your users have raised, with its customs and billing state.'}
                  />

                  <Panel className="px-4 sm:px-5 py-4 mb-5">
                    <div className="flex flex-col xl:flex-row xl:items-center gap-4">
                      <div className="flex flex-wrap items-center gap-2 self-start">
                        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800/70">
                          {[
                            { id: 'Air-Export', label: 'Air Export', count: shipments.length },
                            { id: 'Sea-Export', label: 'Sea Export', count: seaExportShipments.length },
                          ].map((t) => {
                            const on = shipmentType === t.id;
                            return (
                              <button key={t.id} type="button" onClick={() => setShipmentType(t.id)} className={segment(on)}>
                                {t.label}
                                <span className={`px-1.5 rounded-md text-[11px] ${on ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700'}`}>{t.count}</span>
                              </button>
                            );
                          })}
                        </div>

                      </div>

                      {(
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
                          {miniStats.map((m) => (
                            <button key={m.id} type="button" onClick={m.apply || undefined} disabled={!m.apply}
                              title={m.apply ? `Show ${m.label.toLowerCase()}` : undefined}
                              className={`relative overflow-hidden rounded-xl border px-3.5 pt-2.5 pb-3 text-left transition
                                          ${!m.apply ? 'cursor-default ' : ''}${m.on
                                            ? 'border-blue-400 bg-blue-50 dark:border-blue-500/60 dark:bg-blue-500/10 ring-2 ring-blue-500/20'
                                            : 'border-slate-200 bg-slate-50/70 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-slate-800/60'}`}>
                              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{m.label}</p>
                              <div className={`mt-1 ${m.apply ? 'flex items-baseline gap-2' : ''}`}>
                                <span className={`leading-none font-black text-slate-900 dark:text-white
                                                  ${m.apply ? 'text-[22px]' : 'text-[19px]'}`}>{m.value}</span>
                                <span className={`block text-[11px] font-bold ${m.tone} ${m.apply ? '' : 'mt-1'}`}>{m.note}</span>
                              </div>
                              <span className={`absolute left-0 right-0 bottom-0 h-[3px] ${m.bar}`} />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </Panel>

                  {(
                    <>
                      <ShipmentFilters
                        user={{
                          value: selectedCustomer,
                          onChange: setSelectedCustomer,
                          options: customers.map((c) => ({ value: c.name, label: c.name })),
                        }}
                        dateLabel={dateRange ? formatDateRange() : 'All dates'}
                        onOpenDates={() => setShowDatePicker(true)}
                        paymentFilter={paymentFilter}
                        onPaymentFilter={setPaymentFilter}
                        unpaidSince={unpaidSince}
                        onUnpaidSince={setUnpaidSince}
                        unpaidOpen={showUnpaidSincePicker}
                        onUnpaidOpen={setShowUnpaidSincePicker}
                        overdueDays={overdueDays}
                        onOverdueDays={setOverdueDays}
                        facets={facets}
                        onReset={resetAll}
                        actions={(
                          <>
                            <button type="button" className={btn.quiet}
                              title="The invoices raised against a customer, with no shipment"
                              onClick={() => setCustomerInvoiceListOpen(true)}>
                              <Receipt size={16} /> Customer invoices
                            </button>
                            <button type="button" className={btn.green}
                              onClick={() => {
                                if (isAir) exportShipmentsExcel(filteredAirShipments, invoiceMap);
                                else exportToCSV(filteredSeaShipments, `shipments_${new Date().toISOString().split('T')[0]}.csv`);
                              }}>
                              <Download size={16} /> {isAir ? 'Export Excel' : 'Export CSV'}
                            </button>
                          </>
                        )}
                      />
                    </>
                  )}
                </>
              );
            })()}

            {shipmentType === 'Air-Export' ? (
              shipmentsLoading ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-12 flex items-center justify-center dark:border-slate-800 dark:bg-slate-900/60">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600 dark:text-slate-400">Loading shipments...</p>
                  </div>
                </div>
              ) : (
                <>
                  <ShipmentsTable
                    shipments={filteredAirShipments}
                    userRole="admin"
                    invoiceMap={invoiceMap}
                    showTotals
                  />
                  <div ref={airSentinelRef} className="h-8" />
                  {airLoadingMore && (
                    <div className="flex items-center justify-center gap-3 py-4 text-sm text-slate-500 dark:text-slate-400">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      Loading more…
                    </div>
                  )}
                  {!airHasMore && shipments.length > 0 && !airLoadingMore && (
                    <p className="text-center text-xs text-slate-400 dark:text-slate-500 py-3">
                      All {shipments.length} air export shipments loaded
                    </p>
                  )}
                </>
              )
            ) : (
              <>
                <SeaExportTableReadOnly
                  shipments={filteredSeaShipments}
                  loading={loadingSeaExport}
                />
                <div ref={seaSentinelRef} className="h-8" />
                {seaLoadingMore && (
                  <div className="flex items-center justify-center gap-3 py-4 text-sm text-slate-500 dark:text-slate-400">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    Loading more…
                  </div>
                )}
                {!seaHasMore && seaExportShipments.length > 0 && !seaLoadingMore && (
                  <p className="text-center text-xs text-slate-400 dark:text-slate-500 py-3">
                    All {seaExportShipments.length} sea export shipments loaded
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Invoices Section */}
        {activeTab === 'invoices' && (
          <ClientMaster
            role="admin"
            search={clientSearch}
            onSearch={setClientSearch}
            customers={customers}
            onEditInvoice={(invoice) => setCustomerInvoiceEdit(invoice)}
          />
        )}

        {/* Insights Section */}
        {activeTab === 'insights' && (
          <div className="space-y-5">
            <PageHead
              eyebrow="Admin · Analytics"
              title="Sales Insights"
              badge={<Pill tone="violet">{totalInvoices} invoices</Pill>}
              subtitle="What your users have billed — by month, by customer and by service."
            />

            {insightsLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-slate-600 dark:text-slate-400">Loading insights...</span>
              </div>
            ) : (
              <>
                {/* Top Summary Cards - 6 Cards */}
                <div className="relative isolate z-[20] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">

                  {/* ── Card 1: Total Sales ── */}
                  <div className="group relative z-10 hover:z-[30] overflow-visible rounded-xl border px-3.5 pt-3 pb-4 cursor-default transition
                                    border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-slate-800/60">
                    <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-blue-500" />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Total Sales</span>
                      <span className="grid place-items-center w-7 h-7 rounded-lg flex-shrink-0 bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300"><IndianRupee size={14} /></span>
                    </div>
                    <p className="mt-1.5 text-[22px] leading-none font-black text-slate-900 dark:text-white">₹{(totalRevenue / 100000).toFixed(1)}L</p>
                    <p className="text-[11px] font-bold text-blue-600 dark:text-blue-300 mt-1.5">{totalInvoices} invoices</p>
                    {/* Tooltip */}
                    <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-[calc(100%+10px)] z-[40] w-64 opacity-0 group-hover:opacity-100 transition-all duration-200 -translate-y-1 group-hover:translate-y-0">
                      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-4 shadow-2xl text-sm">
                        <p className="text-white font-bold mb-3 border-b border-slate-700 pb-2 flex items-center gap-2">
                          <IndianRupee size={14} className="text-blue-400" /> Sales Breakdown
                        </p>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-blue-300">Total Revenue</span>
                            <span className="text-white font-semibold">₹{totalRevenue.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-300">Total Invoices</span>
                            <span className="text-white font-semibold">{totalInvoices}</span>
                          </div>
                          <div className="flex justify-between border-t border-slate-700 pt-2">
                            <span className="text-amber-400">Avg / Invoice</span>
                            <span className="text-white font-semibold">₹{avgOrderValue.toLocaleString('en-IN')}</span>
                          </div>
                          {monthWiseData.length > 0 && (() => {
                            const best = [...monthWiseData].sort((a, b) => b.revenue - a.revenue)[0];
                            return (
                              <div className="flex justify-between">
                                <span className="text-emerald-400">🏆 Best Month</span>
                                <span className="text-white font-semibold">{best.month}</span>
                              </div>
                            );
                          })()}
                          {monthWiseData.length > 0 && (() => {
                            const worst = [...monthWiseData].sort((a, b) => a.revenue - b.revenue)[0];
                            return (
                              <div className="flex justify-between">
                                <span className="text-rose-400">📉 Lowest Month</span>
                                <span className="text-white font-semibold">{worst.month}</span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-slate-900 border-l border-t border-slate-700 rotate-45 absolute -top-1.5 left-1/2 -translate-x-1/2" />
                    </div>
                  </div>

                  {/* ── Card 2: Avg Order Value ── */}
                  <div className="group relative z-10 hover:z-[30] overflow-visible rounded-xl border px-3.5 pt-3 pb-4 cursor-default transition
                                    border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-slate-800/60">
                    <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-emerald-500" />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Avg Order</span>
                      <span className="grid place-items-center w-7 h-7 rounded-lg flex-shrink-0 bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300"><TrendingUp size={14} /></span>
                    </div>
                    <p className="mt-1.5 text-[22px] leading-none font-black text-slate-900 dark:text-white">₹{avgOrderValue.toLocaleString('en-IN')}</p>
                    <p className="text-[11px] font-bold text-emerald-600 dark:text-emerald-300 mt-1.5">per invoice</p>
                    {/* Tooltip */}
                    <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-[calc(100%+10px)] z-[40] w-64 opacity-0 group-hover:opacity-100 transition-all duration-200 -translate-y-1 group-hover:translate-y-0">
                      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-4 shadow-2xl text-sm">
                        <p className="text-white font-bold mb-3 border-b border-slate-700 pb-2 flex items-center gap-2">
                          <TrendingUp size={14} className="text-emerald-400" /> Order Value Stats
                        </p>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-emerald-300">Avg Order Value</span>
                            <span className="text-white font-semibold">₹{avgOrderValue.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-emerald-300">Total Invoices</span>
                            <span className="text-white font-semibold">{totalInvoices}</span>
                          </div>
                          <div className="flex justify-between border-t border-slate-700 pt-2">
                            <span className="text-amber-400">Total Revenue</span>
                            <span className="text-white font-semibold">₹{(totalRevenue / 100000).toFixed(2)}L</span>
                          </div>
                          {monthWiseData.length > 0 && (() => {
                            const avgArr = monthWiseData.map(m => m.revenue / ((m.invoices?.length) || 1));
                            const maxAvg = Math.max(...avgArr);
                            const bestIdx = avgArr.indexOf(maxAvg);
                            return (
                              <div className="flex justify-between">
                                <span className="text-cyan-400">📅 Best Avg Month</span>
                                <span className="text-white font-semibold">{monthWiseData[bestIdx]?.month}</span>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-slate-900 border-l border-t border-slate-700 rotate-45 absolute -top-1.5 left-1/2 -translate-x-1/2" />
                    </div>
                  </div>

                  {/* ── Card 3: Total Customers ── */}
                  <div className="group relative z-10 hover:z-[30] overflow-visible rounded-xl border px-3.5 pt-3 pb-4 cursor-default transition
                                    border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-slate-800/60">
                    <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-violet-500" />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Customers</span>
                      <span className="grid place-items-center w-7 h-7 rounded-lg flex-shrink-0 bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300"><Users size={14} /></span>
                    </div>
                    <p className="mt-1.5 text-[22px] leading-none font-black text-slate-900 dark:text-white">{customerWiseData.length}</p>
                    <p className="text-[11px] font-bold text-violet-600 dark:text-violet-300 mt-1.5">unique buyers</p>
                    {/* Tooltip */}
                    <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-[calc(100%+10px)] z-[40] w-68 w-[270px] opacity-0 group-hover:opacity-100 transition-all duration-200 -translate-y-1 group-hover:translate-y-0">
                      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-4 shadow-2xl text-sm">
                        <p className="text-white font-bold mb-3 border-b border-slate-700 pb-2 flex items-center gap-2">
                          <Users size={14} className="text-purple-400" /> Top 5 Customers
                        </p>
                        <div className="space-y-2">
                          {customerWiseData.slice(0, 5).map((c, i) => (
                            <div key={c.name} className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className="text-xs font-bold text-slate-500 w-4">{i + 1}.</span>
                                <span className="text-slate-200 truncate text-xs">{c.name}</span>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-white font-semibold text-xs">₹{(c.revenue / 1000).toFixed(0)}K</p>
                                <p className="text-purple-400 text-xs">{c.invoices?.length || 0} inv</p>
                              </div>
                            </div>
                          ))}
                          <div className="flex justify-between border-t border-slate-700 pt-2">
                            <span className="text-amber-400">Total Unique</span>
                            <span className="text-white font-semibold">{customerWiseData.length} customers</span>
                          </div>
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-slate-900 border-l border-t border-slate-700 rotate-45 absolute -top-1.5 left-1/2 -translate-x-1/2" />
                    </div>
                  </div>

                  {/* ── Card 4: Total Products ── */}
                  <div className="group relative z-10 hover:z-[30] overflow-visible rounded-xl border px-3.5 pt-3 pb-4 cursor-default transition
                                    border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-slate-800/60">
                    <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-amber-500" />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Products</span>
                      <span className="grid place-items-center w-7 h-7 rounded-lg flex-shrink-0 bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300"><Package size={14} /></span>
                    </div>
                    <p className="mt-1.5 text-[22px] leading-none font-black text-slate-900 dark:text-white">{totalProducts}</p>
                    <p className="text-[11px] font-bold text-amber-600 dark:text-amber-300 mt-1.5">items sold</p>
                    {/* Tooltip */}
                    <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-[calc(100%+10px)] z-[40] w-[270px] opacity-0 group-hover:opacity-100 transition-all duration-200 -translate-y-1 group-hover:translate-y-0">
                      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-4 shadow-2xl text-sm">
                        <p className="text-white font-bold mb-3 border-b border-slate-700 pb-2 flex items-center gap-2">
                          <Package size={14} className="text-orange-400" /> Top 5 Products / Services
                        </p>
                        <div className="space-y-2">
                          {productWiseData.slice(0, 5).map((p, i) => (
                            <div key={p.name} className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className="text-xs font-bold text-slate-500 w-4">{i + 1}.</span>
                                <span className="text-slate-200 truncate text-xs">{p.name}</span>
                              </div>
                              <span className="text-white font-semibold text-xs flex-shrink-0">₹{(p.value / 1000).toFixed(0)}K</span>
                            </div>
                          ))}
                          <div className="flex justify-between border-t border-slate-700 pt-2">
                            <span className="text-amber-400">Total Line Items</span>
                            <span className="text-white font-semibold">{totalProducts}</span>
                          </div>
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-slate-900 border-l border-t border-slate-700 rotate-45 absolute -top-1.5 left-1/2 -translate-x-1/2" />
                    </div>
                  </div>

                  {/* ── Card 5: Top Customer ── */}
                  <div className="group relative z-10 hover:z-[30] overflow-visible rounded-xl border px-3.5 pt-3 pb-4 cursor-default transition
                                    border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-slate-800/60">
                    <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-pink-500" />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Top Customer</span>
                      <span className="grid place-items-center w-7 h-7 rounded-lg flex-shrink-0 bg-pink-50 text-pink-600 dark:bg-pink-500/15 dark:text-pink-300"><Users size={14} /></span>
                    </div>
                    <p className="mt-1.5 text-[15px] truncate leading-none font-black text-slate-900 dark:text-white" title={topCustomer.name}>{topCustomer.name.substring(0, 12)}{topCustomer.name.length > 12 ? '...' : ''}</p>
                    <p className="text-[11px] font-bold text-pink-600 dark:text-pink-300 mt-1.5">₹{(topCustomer.amount / 1000).toFixed(0)}K</p>
                    {/* Tooltip */}
                    <div className="pointer-events-none absolute right-0 top-[calc(100%+10px)] z-[40] w-[270px] opacity-0 group-hover:opacity-100 transition-all duration-200 -translate-y-1 group-hover:translate-y-0">
                      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-4 shadow-2xl text-sm">
                        <p className="text-white font-bold mb-3 border-b border-slate-700 pb-2 flex items-center gap-2">
                          <Users size={14} className="text-pink-400" /> Top Customer Details
                        </p>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-pink-300">Name</span>
                            <span className="text-white font-semibold text-xs text-right max-w-[55%] truncate" title={topCustomer.name}>{topCustomer.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-pink-300">Total Revenue</span>
                            <span className="text-white font-semibold">₹{topCustomer.amount.toLocaleString('en-IN')}</span>
                          </div>
                          {customerWiseData[0] && (
                            <div className="flex justify-between">
                              <span className="text-pink-300">Invoices</span>
                              <span className="text-white font-semibold">{customerWiseData[0].invoices?.length || 0}</span>
                            </div>
                          )}
                          <div className="flex justify-between border-t border-slate-700 pt-2">
                            <span className="text-amber-400">% of Total Sales</span>
                            <span className="text-white font-semibold">{totalRevenue ? ((topCustomer.amount / totalRevenue) * 100).toFixed(1) : 0}%</span>
                          </div>
                          <div className="mt-2">
                            <p className="text-slate-500 text-xs mb-1">Runner-ups</p>
                            {customerWiseData.slice(1, 4).map((c, i) => (
                              <div key={c.name} className="flex justify-between text-xs">
                                <span className="text-slate-400 truncate max-w-[55%]">{i + 2}. {c.name}</span>
                                <span className="text-slate-300">₹{(c.revenue / 1000).toFixed(0)}K</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-slate-900 border-l border-t border-slate-700 rotate-45 absolute -top-1.5 right-6" />
                    </div>
                  </div>

                  {/* ── Card 6: Top Product ── */}
                  <div className="group relative z-10 hover:z-[30] overflow-visible rounded-xl border px-3.5 pt-3 pb-4 cursor-default transition
                                    border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-slate-800/60">
                    <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-cyan-500" />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Top Product</span>
                      <span className="grid place-items-center w-7 h-7 rounded-lg flex-shrink-0 bg-cyan-50 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-300"><BarChart3 size={14} /></span>
                    </div>
                    <p className="mt-1.5 text-[15px] truncate leading-none font-black text-slate-900 dark:text-white" title={topProduct.name}>{topProduct.name.substring(0, 12)}{topProduct.name.length > 12 ? '...' : ''}</p>
                    <p className="text-[11px] font-bold text-cyan-600 dark:text-cyan-300 mt-1.5">₹{(topProduct.amount / 1000).toFixed(0)}K</p>
                    {/* Tooltip */}
                    <div className="pointer-events-none absolute right-0 top-[calc(100%+10px)] z-[40] w-[270px] opacity-0 group-hover:opacity-100 transition-all duration-200 -translate-y-1 group-hover:translate-y-0">
                      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-4 shadow-2xl text-sm">
                        <p className="text-white font-bold mb-3 border-b border-slate-700 pb-2 flex items-center gap-2">
                          <BarChart3 size={14} className="text-cyan-400" /> Top Product / Service
                        </p>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-cyan-300">Service / Product</span>
                            <span className="text-white font-semibold text-xs text-right max-w-[55%] truncate" title={topProduct.name}>{topProduct.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-cyan-300">Revenue</span>
                            <span className="text-white font-semibold">₹{topProduct.amount.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex justify-between border-t border-slate-700 pt-2">
                            <span className="text-amber-400">% of Total Sales</span>
                            <span className="text-white font-semibold">{totalRevenue ? ((topProduct.amount / totalRevenue) * 100).toFixed(1) : 0}%</span>
                          </div>
                          <div className="mt-2">
                            <p className="text-slate-500 text-xs mb-1">Other Products</p>
                            {productWiseData.slice(1, 4).map((p, i) => (
                              <div key={p.name} className="flex justify-between text-xs">
                                <span className="text-slate-400 truncate max-w-[60%]">{i + 2}. {p.name}</span>
                                <span className="text-slate-300">₹{(p.value / 1000).toFixed(0)}K</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="w-3 h-3 bg-slate-900 border-l border-t border-slate-700 rotate-45 absolute -top-1.5 right-6" />
                    </div>
                  </div>

                </div>

                {/* Main Content Grid */}
                <div className="relative z-0 grid grid-cols-1 xl:grid-cols-3 gap-6">
                  {/* Left Column - Charts */}
                  <div className="xl:col-span-2 space-y-6">
                    {/* Month-wise Revenue Chart - Always Visible */}
                    <div className="rounded-2xl border p-5 border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/60">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                          <TrendingUp className="text-blue-500" size={20} />
                          Monthly Revenue Trend
                        </h3>
                        <span className="text-xs text-slate-500 dark:text-slate-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                          Click bar to drill down ↓
                        </span>
                      </div>
                      {monthWiseData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={monthWiseData} onClick={handleMonthClick} margin={{ top: 10, right: 30, left: -10, bottom: 0 }} barCategoryGap="40%">
                            <defs>
                              {monthWiseData.map((entry, index) => (
                                <linearGradient key={`gradient-${index}`} id={`colorBar${index}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={COLORS[index % COLORS.length]} stopOpacity={1}/>
                                  <stop offset="100%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0.6}/>
                                </linearGradient>
                              ))}
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" vertical={false} />
                            <XAxis dataKey="month" tick={{ fill: '#e5e7eb', fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.08)' }} />
                            <Bar dataKey="revenue" radius={[6, 6, 0, 0]} cursor="pointer" maxBarSize={80}>
                              <LabelList 
                                dataKey="revenue" 
                                position="top" 
                                formatter={(value) => {
                                  const total = monthWiseData.reduce((sum, item) => sum + item.revenue, 0);
                                  const percent = total > 0 ? ((value / total) * 100).toFixed(0) : 0;
                                  return `${percent}%`;
                                }}
                                style={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                              />
                              {monthWiseData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={`url(#colorBar${index})`} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-[280px] text-slate-400">
                          <BarChart3 size={48} className="mb-2 opacity-50" />
                          <p className="text-sm">No data available</p>
                        </div>
                      )}
                    </div>

                    {/* Drill-down: Customer Chart - Shows below when month selected */}
                    {selectedMonth && (
                      <div className="rounded-2xl border p-5 shadow-sm animate-fadeIn bg-white border-emerald-200 dark:bg-slate-900/60 dark:border-emerald-500/30">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <Users className="text-green-500" size={20} />
                            Customers in <span className="text-green-600 dark:text-green-400">{selectedMonth}</span>
                          </h3>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-full">
                              Click bar to see products ↓
                            </span>
                            <button onClick={() => { setSelectedMonth(null); setInsightCustomer(null); setDrillDownCustomerData([]); setDrillDownProductData([]); }} className="text-xs text-red-500 hover:text-red-700">✕ Close</button>
                          </div>
                        </div>
                        {drillDownCustomerData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={Math.min(drillDownCustomerData.length * 45 + 40, 300)}>
                            <BarChart data={drillDownCustomerData} layout="vertical" onClick={handleCustomerClick} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" horizontal={false} />
                              <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} axisLine={false} />
                              <YAxis type="category" dataKey="name" tick={{ fill: '#e5e7eb', fontSize: 12, fontWeight: 600 }} width={180} axisLine={false} tickFormatter={(name) => name.length > 20 ? name.substring(0, 18) + '...' : name} />
                              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(16, 185, 129, 0.08)' }} />
                              <Bar dataKey="revenue" radius={[0, 6, 6, 0]} cursor="pointer">
                                <LabelList 
                                  dataKey="revenue" 
                                  position="right" 
                                  formatter={(value) => {
                                    const total = drillDownCustomerData.reduce((sum, item) => sum + item.revenue, 0);
                                    const percent = total > 0 ? ((value / total) * 100).toFixed(0) : 0;
                                    return `${percent}%`;
                                  }}
                                  style={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                                />
                                {drillDownCustomerData.map((_, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-[120px] text-slate-400">
                            <p className="text-sm">No customer data</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Drill-down: Product Chart - Shows below when customer selected */}
                    {insightCustomer && (
                      <div className="rounded-2xl border p-5 shadow-sm animate-fadeIn bg-white border-violet-200 dark:bg-slate-900/60 dark:border-violet-500/30">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <PieChart className="text-purple-500" size={20} />
                            Products for <span className="text-purple-600 dark:text-purple-400">{insightCustomer.length > 20 ? insightCustomer.substring(0, 20) + '...' : insightCustomer}</span>
                          </h3>
                          <button onClick={() => { setInsightCustomer(null); setDrillDownProductData([]); }} className="text-xs text-red-500 hover:text-red-700">✕ Close</button>
                        </div>
                        {drillDownProductData.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <ResponsiveContainer width="100%" height={240}>
                              <RechartsPie>
                                <Pie 
                                  data={drillDownProductData} 
                                  cx="50%" 
                                  cy="55%" 
                                  outerRadius={70} 
                                  innerRadius={40} 
                                  dataKey="value" 
                                  paddingAngle={2}
                                  label={({ value, percent }) => {
                                    return `${(percent * 100).toFixed(0)}%`;
                                  }}
                                  labelLine={true}
                                >
                                  {drillDownProductData.map((_, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip isPie={true} />} />
                              </RechartsPie>
                            </ResponsiveContainer>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                              {drillDownProductData.map((item, index) => {
                                const total = drillDownProductData.reduce((sum, i) => sum + i.value, 0);
                                const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                                return (
                                  <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                                    <span className="text-xs text-slate-700 dark:text-slate-300 flex-1 truncate">{item.fullName}</span>
                                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">{percent}%</span>
                                    <span className="text-xs font-bold text-slate-900 dark:text-white">₹{item.value.toLocaleString('en-IN')}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-[120px] text-slate-400">
                            <p className="text-sm">No product data</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right Column - Month Growth Table */}
                  <div className="space-y-6">
                    {/* Month-wise Growth Table */}
                    <div className="rounded-2xl border p-5 border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/60">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                        <TrendingUp className="text-blue-500" size={20} />
                        Month-wise Growth
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-800">
                              <th className="text-left py-2 px-2 text-slate-600 dark:text-slate-400 font-medium">Month</th>
                              <th className="text-right py-2 px-2 text-slate-600 dark:text-slate-400 font-medium">Revenue</th>
                              <th className="text-right py-2 px-2 text-slate-600 dark:text-slate-400 font-medium">Growth</th>
                            </tr>
                          </thead>
                          <tbody>
                            {monthWiseData.map((item, index) => (
                              <tr key={index} className="border-b border-slate-100 dark:border-slate-800/70 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer" onClick={() => handleMonthClick({ activePayload: [{ payload: item }] })}>
                                <td className="py-2.5 px-2 font-medium text-slate-900 dark:text-white">{item.month}</td>
                                <td className="py-2.5 px-2 text-right text-slate-700 dark:text-slate-300">₹{(item.revenue / 1000).toFixed(1)}K</td>
                                <td className="py-2.5 px-2 text-right">
                                  {index === 0 ? (
                                    <span className="text-slate-400">-</span>
                                  ) : (
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                      item.growth > 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                      item.growth < 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                      'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                                    }`}>
                                      {item.growth > 0 ? '↑' : item.growth < 0 ? '↓' : '→'} {Math.abs(item.growth)}%
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                            {monthWiseData.length === 0 && (
                              <tr><td colSpan="3" className="py-8 text-center text-slate-400">No data</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Top Products Quick View */}
                    <div className="rounded-2xl border p-5 border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/60">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                        <BarChart3 className="text-purple-500" size={20} />
                        Top Products
                      </h3>
                      <div className="space-y-3">
                        {productWiseData.slice(0, 5).map((product, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-700' :
                              index === 1 ? 'bg-slate-200 text-slate-600' :
                              index === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                            }`}>{index + 1}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-slate-700 dark:text-slate-300 truncate">{product.fullName}</p>
                              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-1 mt-1">
                                <div className="h-1 rounded-full" style={{ width: `${(product.value / productWiseData[0].value) * 100}%`, backgroundColor: COLORS[index % COLORS.length] }}></div>
                              </div>
                            </div>
                            <span className="text-xs font-bold text-slate-900 dark:text-white">₹{(product.value / 1000).toFixed(0)}K</span>
                          </div>
                        ))}
                        {productWiseData.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No data</p>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* User Performance Table */}
                <div className="rounded-2xl border p-5 border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/60">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                    <Users className="text-emerald-500" size={20} />
                    User Performance
                    <span className="text-xs font-normal text-slate-500 dark:text-slate-400 ml-2">(Sorted by Sales)</span>
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800/60">
                          <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">#</th>
                          <th className="text-left py-3 px-4 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">User Name</th>
                          <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Shipments</th>
                          <th className="text-center py-3 px-4 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Total Weight</th>
                          <th className="text-right py-3 px-4 text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Total Sales</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {userPerformanceData.map((user, index) => (
                          <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                            <td className="py-3 px-4">
                              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white' :
                                index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-white' :
                                index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                                'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                              }`}>{index + 1}</span>
                            </td>
                            <td className="py-3 px-4">
                              <p className="font-medium text-slate-900 dark:text-white text-sm" title={user.name}>{user.displayName}</p>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                {user.shipments}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                                {user.weight} kg
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-bold text-slate-900 dark:text-white">₹{user.sales.toLocaleString('en-IN')}</span>
                            </td>
                          </tr>
                        ))}
                        {userPerformanceData.length === 0 && (
                          <tr>
                            <td colSpan="5" className="py-8 text-center text-slate-400">
                              No user data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Bottom Section - Customer & Product Analysis */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Customers with Chart */}
                  <div className="rounded-2xl border p-5 border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/60">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <Users className="text-green-500" size={20} />
                      Top Customers
                    </h3>
                    {customerWiseData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={customerWiseData.slice(0, 6)} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                          <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 10 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} axisLine={false} />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            tick={{ fill: '#e5e7eb', fontSize: 11, fontWeight: 600 }} 
                            width={140} 
                            axisLine={false}
                            tickFormatter={(name) => name && name.length > 16 ? name.substring(0, 14) + '...' : name}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                            {customerWiseData.slice(0, 6).map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[250px] text-slate-400">
                        <p className="text-sm">No customer data</p>
                      </div>
                    )}
                  </div>

                  {/* Product Distribution Pie */}
                  <div className="rounded-2xl border p-5 border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/60">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                      <PieChart className="text-purple-500" size={20} />
                      Product Distribution
                    </h3>
                    {productWiseData.length > 0 ? (
                      <div className="flex items-center gap-4">
                        <ResponsiveContainer width="50%" height={220}>
                          <RechartsPie>
                            <Pie data={productWiseData.slice(0, 6)} cx="50%" cy="50%" outerRadius={80} innerRadius={50} dataKey="value" paddingAngle={2}>
                              {productWiseData.slice(0, 6).map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip isPie={true} />} />
                          </RechartsPie>
                        </ResponsiveContainer>
                        <div className="flex-1 space-y-2">
                          {productWiseData.slice(0, 6).map((item, index) => (
                            <div key={index} className="flex items-center gap-2 text-xs">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                              <span className="text-slate-600 dark:text-slate-400 truncate flex-1">{item.name}</span>
                              <span className="font-medium text-slate-900 dark:text-white">{((item.value / totalRevenue) * 100).toFixed(1)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-[220px] text-slate-400">
                        <p className="text-sm">No product data</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <Modal
        open={showEditUserModal}
        onClose={closeModals}
        icon={UserCog}
        tone="blue"
        title="Edit user"
        subtitle={selectedUser?.name || selectedUser?.username}
        width="max-w-3xl"
        footer={(
          <div className="flex flex-col-reverse sm:flex-row gap-2.5">
            <button type="button" onClick={closeModals} className={btn.quiet}>Cancel</button>
            <button type="submit" form="admin-edit-user" className={`${btn.primary} flex-1 justify-center`}>
              <Save size={16} /> Update user
            </button>
          </div>
        )}
      >
        <form id="admin-edit-user" onSubmit={handleUpdateUser}>
          <FormSection title="Identity" description="Who the account belongs to." icon={UserIcon} tone="blue">
            <FormField label="Customer name" required error={errors.customerName} className="md:col-span-2">
              <input
                type="text"
                value={editUser.customerName}
                onChange={(e) => { setEditUser({ ...editUser, customerName: e.target.value }); setErrors({ ...errors, customerName: '' }); }}
                className={`${field} ${errors.customerName ? 'border-rose-400' : ''}`}
              />
            </FormField>

            <FormField label="Email" required error={errors.email}>
              <input
                type="email"
                value={editUser.email}
                onChange={(e) => { setEditUser({ ...editUser, email: e.target.value }); setErrors({ ...errors, email: '' }); }}
                className={`${field} ${errors.email ? 'border-rose-400' : ''}`}
              />
            </FormField>

            <FormField label="Phone number" required error={errors.phoneNumber}>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] font-bold text-slate-400 pointer-events-none">+91</span>
                <input
                  type="tel"
                  value={editUser.phoneNumber}
                  onChange={(e) => { setEditUser({ ...editUser, phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 10) }); setErrors({ ...errors, phoneNumber: '' }); }}
                  className={`${field} pl-12 ${errors.phoneNumber ? 'border-rose-400' : ''}`}
                />
              </div>
            </FormField>

            <FormField label="Website" hint="(optional)" error={errors.website}>
              <input
                type="text"
                value={editUser.website}
                onChange={(e) => { setEditUser({ ...editUser, website: e.target.value }); setErrors({ ...errors, website: '' }); }}
                placeholder="https://example.com"
                className={`${field} ${errors.website ? 'border-rose-400' : ''}`}
              />
            </FormField>
          </FormSection>

          <FormSection title="Access" description="What they sign in with, and what they may reach." icon={KeyRound} tone="amber">
            <FormField label="Username" required error={errors.username}>
              <input
                type="text"
                value={editUser.username}
                onChange={(e) => { setEditUser({ ...editUser, username: e.target.value }); setErrors({ ...errors, username: '' }); }}
                className={`${field} ${errors.username ? 'border-rose-400' : ''}`}
              />
            </FormField>

            <FormField label="Password" hint="(leave blank to keep current)" error={errors.password}>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={editUser.password}
                  onChange={(e) => { setEditUser({ ...editUser, password: e.target.value }); setErrors({ ...errors, password: '' }); }}
                  className={`${field} pr-11 ${errors.password ? 'border-rose-400' : ''}`}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-400
                             hover:text-slate-700 hover:bg-slate-100 dark:hover:text-slate-100 dark:hover:bg-slate-700 transition">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </FormField>

            {/* An admin may only move an account between the roles it owns. */}
            <FormField label="Role" required>
              <Select
                clearable={false}
                value={editUser.role}
                onChange={(v) => setEditUser({ ...editUser, role: v })}
                options={['user', 'accountant', 'employee'].map((r) => ({
                  value: r,
                  label: r === 'user' ? 'User' : r.charAt(0).toUpperCase() + r.slice(1),
                }))}
              />
            </FormField>

            <FormField label="Status" required>
              <Select
                clearable={false}
                value={editUser.status}
                onChange={(v) => setEditUser({ ...editUser, status: v })}
                options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
              />
            </FormField>
          </FormSection>
        </form>
      </Modal>

      {showDeleteConfirm && (
        <ConfirmModal
          open
          onClose={closeModals}
          onConfirm={confirmDeleteUser}
          title="Delete user"
          subtitle={`${selectedUser?.name || 'This account'} loses access immediately.`}
          confirmLabel="Delete user"
        >
          <p className="text-[13px] text-slate-600 dark:text-slate-300">
            Deleting <span className="font-bold text-slate-900 dark:text-white">{selectedUser?.name}</span> cannot be undone.
            Their shipments and invoices stay where they are; only the account goes.
          </p>
        </ConfirmModal>
      )}

      {showDatePicker && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setShowDatePicker(false)}>
          <div className="rounded-2xl shadow-2xl overflow-hidden max-w-fit max-h-[90vh] flex flex-col
                          bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-6 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
              <div>
                <p className="text-[14px] font-bold text-slate-900 dark:text-white">Shipment dates</p>
                <p className="text-[11.5px] text-slate-500 dark:text-slate-400">
                  Pick a day for that day alone, or a start and an end for a range.
                </p>
              </div>
              <button type="button" onClick={() => setShowDatePicker(false)}
                className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
                <X size={18} />
              </button>
            </div>

            <div className="ql-daterange overflow-auto flex-1">
              <DateRangePicker
                ranges={dateRange || [{ startDate: new Date(), endDate: new Date(), key: 'selection' }]}
                onChange={(item) => setDateRange([item.selection])}
                staticRanges={DATE_PRESETS}
                inputRanges={[]}
                months={window.innerWidth < 768 ? 1 : 2}
                direction={window.innerWidth < 768 ? 'vertical' : 'horizontal'}
                showSelectionPreview
                moveRangeOnFirstSelection={false}
                minDate={new Date(2020, 0, 1)}
                maxDate={new Date()}
                rangeColors={['#2563eb']}
              />
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-slate-100 dark:border-slate-800">
              <button type="button" className={btn.quiet}
                onClick={() => { setDateRange(null); setShowDatePicker(false); }}>
                Clear dates
              </button>
              <button type="button" className={btn.primary} onClick={() => setShowDatePicker(false)}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerInvoicesModal
        open={customerInvoiceListOpen}
        onClose={() => setCustomerInvoiceListOpen(false)}
        onEdit={(invoice) => setCustomerInvoiceEdit(invoice)}
      />
    </AdminShell>
  );
};

export default AdminDashboard;
